'use client';

import { useCallback, useState } from 'react';
import { LiveNotificationsPanel } from '@/src/components/notifications/live-notifications-panel';
import { useLivePolling } from '@/src/lib/use-live-polling';
import { unreadNotificationCountAction } from '@/src/server/actions/notifications.actions';

const POLL_INTERVAL_MS = 5000;

export function NotificationBell({ count: initialCount = null }: { count?: number | null }) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const fetcher = useCallback(async (): Promise<number> => {
    const result = await unreadNotificationCountAction();
    if (result.error) throw new Error(result.error);
    return result.data?.count ?? 0;
  }, []);

  const { data: liveCount } = useLivePolling<number>({
    fetcher,
    intervalMs: POLL_INTERVAL_MS,
    immediate: true,
  });

  const displayCount = liveCount ?? initialCount ?? 0;

  function togglePanel() {
    setIsPanelOpen((prev) => !prev);
  }

  function closePanel() {
    setIsPanelOpen(false);
  }

  const showBadge = typeof displayCount === 'number' && displayCount > 0;
  const badgeLabel = typeof displayCount === 'number' && displayCount > 99 ? '99+' : displayCount;

  return (
    <>
      <button
        type="button"
        onClick={togglePanel}
        aria-label="Benachrichtigungen"
        aria-expanded={isPanelOpen}
        aria-haspopup="dialog"
        data-tour="notification-bell"
        data-unread-count={displayCount}
        data-live-polling="true"
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] text-[var(--k-color-text-secondary)] shadow-[var(--k-shadow-xs)] transition hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {showBadge ? (
          <span
            data-testid="notification-bell-badge"
            className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--k-color-danger-action)] px-1.5 text-[10px] font-bold text-[var(--k-color-text-on-accent)] ring-2 ring-[var(--k-color-surface)]"
          >
            {badgeLabel}
          </span>
        ) : null}
      </button>
      <LiveNotificationsPanel isOpen={isPanelOpen} onClose={closePanel} />
    </>
  );
}
